import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAdminAuth } from '../contexts/AdminAuthContext';
import type { Roll } from '../types/roll';
import { queryKeys } from '../api/queryKeys';

// --- Types ---

export interface CreateRollPayload {
  roll_id: string;
  roll_name: string;
}

export interface RollApiError {
  status: number;
  message: string;
  type: 'duplicate' | 'validation' | 'not_found' | 'network' | 'server';
}

// --- Query key factory ---

// --- Fetch rolls ---

async function fetchRolls(orderId: string, handleUnauthorized: () => void): Promise<Roll[]> {
  const res = await fetch(`/api/v1/admin/orders/${orderId}/rolls`, {
    credentials: 'include',
  });

  if (res.status === 401 || res.status === 403) {
    handleUnauthorized();
    throw new Error('Unauthorized');
  }

  if (res.status === 404) {
    const err: RollApiError = { status: 404, message: 'Order not found', type: 'not_found' };
    throw err;
  }

  if (!res.ok) {
    const err: RollApiError = {
      status: res.status,
      message: 'Could not load rolls',
      type: res.status >= 500 ? 'server' : 'network',
    };
    throw err;
  }

  const data = await res.json();
  return data.rolls ?? data;
}

// --- Create roll ---

async function createRoll(
  orderId: string,
  payload: CreateRollPayload,
  handleUnauthorized: () => void
): Promise<Roll> {
  const res = await fetch(`/api/v1/admin/orders/${orderId}/rolls`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });

  if (res.status === 401 || res.status === 403) {
    handleUnauthorized();
    throw new Error('Unauthorized');
  }

  if (res.status === 409) {
    const err: RollApiError = {
      status: 409,
      message: 'Roll name already exists for this order',
      type: 'duplicate',
    };
    throw err;
  }

  if (res.status === 400) {
    let message = 'Invalid roll name or missing fields';
    try {
      const body = await res.json();
      if (body.message) message = body.message;
      if (body.error) message = body.error;
    } catch {
      // use default message
    }
    const err: RollApiError = { status: 400, message, type: 'validation' };
    throw err;
  }

  if (!res.ok) {
    const err: RollApiError = {
      status: res.status,
      message: 'Something went wrong. Please try again later.',
      type: res.status >= 500 ? 'server' : 'network',
    };
    throw err;
  }

  return res.json();
}

// --- Hooks ---

export function useRollsQuery(orderId: string | undefined) {
  const { handleUnauthorized } = useAdminAuth();

  return useQuery<Roll[], RollApiError>({
    queryKey: queryKeys.admin.rolls(orderId!),
    queryFn: () => fetchRolls(orderId!, handleUnauthorized),
    enabled: !!orderId,
  });
}

export function useCreateRollMutation(
  orderId: string | undefined,
  options?: { onRollCreated?: (roll: Roll) => void }
) {
  const { handleUnauthorized } = useAdminAuth();
  const queryClient = useQueryClient();

  return useMutation<Roll, RollApiError, CreateRollPayload>({
    mutationFn: (payload) => createRoll(orderId!, payload, handleUnauthorized),
    onSuccess: (roll) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.admin.rolls(orderId!) });
      options?.onRollCreated?.(roll);
    },
  });
}

// --- Reset / delete a roll ---

/** Shared error mapping for the roll reset/delete endpoints. */
async function rollMutationError(res: Response): Promise<RollApiError> {
  if (res.status === 404) {
    return { status: 404, message: 'Roll not found', type: 'not_found' };
  }
  return {
    status: res.status,
    message: 'Something went wrong. Please try again later.',
    type: res.status >= 500 ? 'server' : 'network',
  };
}

/** Reset a roll: delete its scans + S3 objects but keep the roll record. */
async function resetRoll(orderId: string, rollId: string, handleUnauthorized: () => void): Promise<void> {
  const res = await fetch(`/api/v1/admin/orders/${orderId}/rolls/${rollId}/reset`, {
    method: 'POST',
    credentials: 'include',
  });
  if (res.status === 401 || res.status === 403) {
    handleUnauthorized();
    throw new Error('Unauthorized');
  }
  if (!res.ok) throw await rollMutationError(res);
}

/** Confirm a single roll: start its (roll-scoped) thumbnail workflow. */
async function confirmRoll(orderId: string, rollId: string, handleUnauthorized: () => void): Promise<void> {
  const res = await fetch(`/api/v1/admin/orders/${orderId}/rolls/${rollId}/confirm`, {
    method: 'POST',
    credentials: 'include',
  });
  if (res.status === 401 || res.status === 403) {
    handleUnauthorized();
    throw new Error('Unauthorized');
  }
  if (!res.ok) throw await rollMutationError(res);
}

/**
 * Rename a roll. Returns the stored (trimmed) name.
 *
 * 409 covers both a name another roll on the order already has and an attempt
 * to rename the customer edits roll; the server's message says which.
 */
async function renameRoll(
  orderId: string,
  rollId: string,
  rollName: string,
  handleUnauthorized: () => void
): Promise<string> {
  const res = await fetch(`/api/v1/admin/orders/${orderId}/rolls/${rollId}/name`, {
    method: 'PUT',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ roll_name: rollName }),
  });

  if (res.status === 401 || res.status === 403) {
    handleUnauthorized();
    throw new Error('Unauthorized');
  }

  if (res.status === 400 || res.status === 409) {
    let message = res.status === 409 ? 'Roll name already exists for this order' : 'Invalid roll name';
    try {
      const body = await res.json();
      if (body.error) message = body.error;
    } catch {
      // use default message
    }
    const err: RollApiError = {
      status: res.status,
      message,
      type: res.status === 409 ? 'duplicate' : 'validation',
    };
    throw err;
  }

  if (!res.ok) throw await rollMutationError(res);

  const body = await res.json();
  return body.roll_name;
}

/**
 * Set (or clear) a roll's lab notes. A blank string clears them.
 *
 * Returns the stored value so the caller can reflect what the server actually
 * persisted (trimmed) rather than the raw textarea contents.
 */
async function updateRollNotes(
  orderId: string,
  rollId: string,
  labNotes: string,
  handleUnauthorized: () => void
): Promise<string | null> {
  const res = await fetch(`/api/v1/admin/orders/${orderId}/rolls/${rollId}/notes`, {
    method: 'PUT',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ lab_notes: labNotes }),
  });

  if (res.status === 401 || res.status === 403) {
    handleUnauthorized();
    throw new Error('Unauthorized');
  }

  if (res.status === 400) {
    let message = 'Lab notes could not be saved';
    try {
      const body = await res.json();
      if (body.error) message = body.error;
    } catch {
      // use default message
    }
    const err: RollApiError = { status: 400, message, type: 'validation' };
    throw err;
  }

  if (!res.ok) throw await rollMutationError(res);

  const body = await res.json();
  return body.lab_notes ?? null;
}

/**
 * Mark (or unmark) a roll as having come back with no recoverable images.
 *
 * Returns the stored flag so the caller reflects what the server actually
 * persisted rather than assuming the checkbox won.
 */
async function updateRollEmpty(
  orderId: string,
  rollId: string,
  isEmpty: boolean,
  handleUnauthorized: () => void
): Promise<boolean> {
  const res = await fetch(`/api/v1/admin/orders/${orderId}/rolls/${rollId}/empty`, {
    method: 'PUT',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ is_empty: isEmpty }),
  });

  if (res.status === 401 || res.status === 403) {
    handleUnauthorized();
    throw new Error('Unauthorized');
  }

  // 409 is the "this roll has scans" / "edits roll" refusal — the server's
  // message names the way out, so surface it rather than a generic failure.
  if (res.status === 409) {
    let message = 'This roll cannot be marked as empty';
    try {
      const body = await res.json();
      if (body.error) message = body.error;
    } catch {
      // use default message
    }
    const err: RollApiError = { status: 409, message, type: 'validation' };
    throw err;
  }

  if (!res.ok) throw await rollMutationError(res);

  const body = await res.json();
  return body.is_empty ?? false;
}

/** Delete a roll entirely: its scans + S3 objects and the roll record. */
async function deleteRoll(orderId: string, rollId: string, handleUnauthorized: () => void): Promise<void> {
  const res = await fetch(`/api/v1/admin/orders/${orderId}/rolls/${rollId}`, {
    method: 'DELETE',
    credentials: 'include',
  });
  if (res.status === 401 || res.status === 403) {
    handleUnauthorized();
    throw new Error('Unauthorized');
  }
  if (!res.ok) throw await rollMutationError(res);
}

/** Invalidate both the rolls list and the order detail (scans) after a roll mutation. */
function invalidateRollAndOrder(queryClient: ReturnType<typeof useQueryClient>, orderId: string) {
  queryClient.invalidateQueries({ queryKey: queryKeys.admin.rolls(orderId) });
  queryClient.invalidateQueries({ queryKey: queryKeys.admin.order(orderId) });
}

export function useConfirmRollMutation(orderId: string | undefined) {
  const { handleUnauthorized } = useAdminAuth();
  const queryClient = useQueryClient();

  return useMutation<void, RollApiError, string>({
    mutationFn: (rollId) => confirmRoll(orderId!, rollId, handleUnauthorized),
    onSuccess: () => invalidateRollAndOrder(queryClient, orderId!),
  });
}

export function useResetRollMutation(orderId: string | undefined) {
  const { handleUnauthorized } = useAdminAuth();
  const queryClient = useQueryClient();

  return useMutation<void, RollApiError, string>({
    mutationFn: (rollId) => resetRoll(orderId!, rollId, handleUnauthorized),
    onSuccess: () => invalidateRollAndOrder(queryClient, orderId!),
  });
}

export function useRenameRollMutation(orderId: string | undefined) {
  const { handleUnauthorized } = useAdminAuth();
  const queryClient = useQueryClient();

  return useMutation<string, RollApiError, { rollId: string; rollName: string }>({
    mutationFn: ({ rollId, rollName }) => renameRoll(orderId!, rollId, rollName, handleUnauthorized),
    onSuccess: () => invalidateRollAndOrder(queryClient, orderId!),
  });
}

export function useUpdateRollNotesMutation(orderId: string | undefined) {
  const { handleUnauthorized } = useAdminAuth();
  const queryClient = useQueryClient();

  return useMutation<string | null, RollApiError, { rollId: string; labNotes: string }>({
    mutationFn: ({ rollId, labNotes }) =>
      updateRollNotes(orderId!, rollId, labNotes, handleUnauthorized),
    onSuccess: () => invalidateRollAndOrder(queryClient, orderId!),
  });
}

export function useUpdateRollEmptyMutation(orderId: string | undefined) {
  const { handleUnauthorized } = useAdminAuth();
  const queryClient = useQueryClient();

  return useMutation<boolean, RollApiError, { rollId: string; isEmpty: boolean }>({
    mutationFn: ({ rollId, isEmpty }) =>
      updateRollEmpty(orderId!, rollId, isEmpty, handleUnauthorized),
    onSuccess: () => invalidateRollAndOrder(queryClient, orderId!),
  });
}

export function useDeleteRollMutation(orderId: string | undefined) {
  const { handleUnauthorized } = useAdminAuth();
  const queryClient = useQueryClient();

  return useMutation<void, RollApiError, string>({
    mutationFn: (rollId) => deleteRoll(orderId!, rollId, handleUnauthorized),
    onSuccess: () => invalidateRollAndOrder(queryClient, orderId!),
  });
}
